import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { API_BASE_URL } from '../config';
import { motion, AnimatePresence } from 'framer-motion';
import {
    getPriceStatusBadgeClass,
    getPriceStatusMessage,
    isPriceStatusLabelsEnabled,
    resolvePriceStatusLabel
} from '../lib/priceIntelligence/featureFlags';
import { formatShoppingDisplayAmount } from '../shared/shoppingDisplayText';

const UNIDADES = ['gr', 'ml', 'adet', 'paket', 'kg', 'lt'];

const getSuggestedPurchaseLabel = (item) => {
    return item?.suggestedPurchase?.display || null;
};

const getNeededLabel = (item) => {
    const needed = item?.needed;
    if (!needed) {
        return formatShoppingDisplayAmount(item?.suggestedAmount || item?.amount || 0, item?.unit || '');
    }
    return formatShoppingDisplayAmount(needed.amount, needed.unit);
};

const ShoppingList = () => {
    const navigate = useNavigate();
    const [currentStepIndex, setCurrentStepIndex] = useState(0);
    const [suggestedSteps, setSuggestedSteps] = useState([]);
    const [selections, setSelections] = useState({}); // { ingredientId: { ...data } }
    const [loading, setLoading] = useState(true);
    const [listName, setListName] = useState(`Alışveriş Listesi (${new Date().toLocaleDateString('tr-TR')})`);
    const [modalItem, setModalItem] = useState(null);
    const [modalSession, setModalSession] = useState(null);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const priceLabelsEnabled = isPriceStatusLabelsEnabled();
    const labelVariant = priceLabelsEnabled ? 'labels_on' : 'labels_off';

    useEffect(() => {
        fetchSuggestions();
    }, []);

    const postPriceLabelEvent = async (eventName, payload = {}) => {
        try {
            const token = localStorage.getItem('authToken');
            if (!token) return;
            await fetch(`${API_BASE_URL}/api/realtime/observe-price-label-event`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    event: eventName,
                    payload: {
                        ...payload,
                        variant: labelVariant
                    }
                })
            });
        } catch (_) {
            // metrics fire-and-forget
        }
    };

    const fetchSuggestions = async () => {
        try {
            setLoading(true);
            const token = localStorage.getItem('authToken');
            const res = await fetch(`${API_BASE_URL}/api/shopping/suggested`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            const data = await res.json();

            // Group suggested items by category to create steps like QuickWizard
            const grouped = {};
            data.forEach(item => {
                const cat = item.category || 'Diğer';
                if (!grouped[cat]) grouped[cat] = [];
                grouped[cat].push(item);
            });

            const steps = Object.keys(grouped).map((cat, idx) => ({
                id: idx + 1,
                title: cat,
                icon: getCategoryIcon(cat),
                items: grouped[cat]
            }));

            // Final Review Step
            steps.push({
                id: steps.length + 1,
                title: 'Tamamla',
                icon: 'fact_check',
                items: [],
                isFinal: true
            });

            setSuggestedSteps(steps);

            // Pre-select items suggested by AI (Critical items or those with positive suggested amounts)
            const initialSelections = {};
            data.forEach(i => {
                if (i.isCritical || (i.suggestedAmount > 0)) {
                    initialSelections[i.ingredientId] = {
                        ...i,
                        amount: i.suggestedAmount || 1,
                        unit: i.unit || 'gr'
                    };
                }
            });
            setSelections(initialSelections);

        } catch (err) {
            console.error('Failed to fetch suggestions');
        } finally {
            setLoading(false);
        }
    };

    const getCategoryIcon = (cat) => {
        const lower = cat.toLowerCase();
        if (lower.includes('sebze')) return 'eco';
        if (lower.includes('meyve')) return 'nutrition';
        if (lower.includes('et') || lower.includes('tavuk')) return 'set_meal';
        if (lower.includes('süt') || lower.includes('kahvaltı')) return 'egg';
        if (lower.includes('kuru')) return 'kitchen';
        return 'inventory_2';
    };

    const handleQuantityConfirm = (amount, unit) => {
        const selectedAmount = parseFloat(amount || 0);
        const suggestedAmount = Number(modalItem?.suggestedAmount || 0);
        const suggestedUnit = String(modalItem?.unit || '').toLowerCase();
        const selectedUnit = String(unit || '').toLowerCase();
        const amountChanged = Number.isFinite(selectedAmount) && Number.isFinite(suggestedAmount)
            ? Math.abs(selectedAmount - suggestedAmount) > 0.001
            : false;
        const unitChanged = suggestedUnit && selectedUnit && suggestedUnit !== selectedUnit;

        setSelections(prev => ({
            ...prev,
            [modalItem.ingredientId]: { ...modalItem, amount, unit }
        }));

        postPriceLabelEvent('add_to_list', {
            ingredientId: modalItem?.ingredientId,
            label: resolvePriceStatusLabel(modalItem) || null
        });
        if (amountChanged || unitChanged) {
            postPriceLabelEvent('alternative_select', {
                ingredientId: modalItem?.ingredientId,
                label: resolvePriceStatusLabel(modalItem) || null
            });
        }

        setModalSession(null);
        setModalItem(null);
    };

    const openItemModal = (item) => {
        setModalItem(item);
        setModalSession({
            ingredientId: item?.ingredientId,
            initiallySelected: Boolean(selections[item?.ingredientId]),
            openedAt: Date.now()
        });
    };

    const closeItemModal = () => {
        const ingredientId = modalSession?.ingredientId;
        const stillSelected = ingredientId ? Boolean(selections[ingredientId]) : false;
        if (ingredientId && !modalSession?.initiallySelected && !stillSelected) {
            postPriceLabelEvent('postpone', {
                ingredientId,
                label: resolvePriceStatusLabel(modalItem) || null
            });
        }
        setModalSession(null);
        setModalItem(null);
    };

    const handleRemove = (id) => {
        setSelections(prev => {
            const next = { ...prev };
            delete next[id];
            return next;
        });
    };

    const handleSave = async () => {
        const selectedItems = Object.values(selections);
        const unresolvedKitchenItems = selectedItems.filter((item) => {
            const itemGroup = String(item.itemGroup || 'KITCHEN').toUpperCase();
            return itemGroup === 'KITCHEN' && !String(item.dictionaryId || item.ingredientId || '').trim();
        });
        if (unresolvedKitchenItems.length > 0) {
            alert('Mutfak ürünleri için önce listeden doğru ürünü seçmelisin.');
            return;
        }
        setIsSubmitting(true);
        try {
            const token = localStorage.getItem('authToken');
            const payload = {
                name: listName,
                items: selectedItems.map(i => ({
                    ingredientId: i.ingredientId,
                    dictionaryId: i.dictionaryId || i.ingredientId || null,
                    name: i.name,
                    suggestedAmount: parseFloat(i.amount),
                    unit: i.unit
                }))
            };

            const res = await fetch(`${API_BASE_URL}/api/shopping/lists`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(payload)
            });

            if (res.ok) {
                navigate('/my-shopping-lists');
            } else {
                const errorPayload = await res.json().catch(() => ({}));
                alert(errorPayload.message || 'Liste kaydedilemedi. Ürün eşleşmelerini kontrol etmelisin.');
            }
        } catch (err) {
            alert('Liste kaydedilemedi');
        } finally {
            setIsSubmitting(false);
        }
    };

    if (loading) return (
        <div className="flex flex-col items-center justify-center p-20 gap-4">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
            <p className="font-bold text-slate-400 animate-pulse">Mutfağındaki Eksikler Hesaplanıyor...</p>
        </div>
    );

    const currentStep = suggestedSteps[currentStepIndex];
    const progress = suggestedSteps.length > 0
        ? ((currentStepIndex + 1) / suggestedSteps.length) * 100
        : 0;
    const modalPriceLabel = priceLabelsEnabled ? resolvePriceStatusLabel(modalItem) : null;

    useEffect(() => {
        if (!currentStep || currentStep.isFinal) return;
        const items = Array.isArray(currentStep.items) ? currentStep.items : [];
        if (items.length === 0) return;
        const labeledCount = items.filter((item) => Boolean(resolvePriceStatusLabel(item))).length;
        postPriceLabelEvent('impression', {
            count: priceLabelsEnabled ? labeledCount : items.length,
            step: currentStepIndex + 1
        });
    }, [currentStepIndex, suggestedSteps, priceLabelsEnabled]);

    if (!currentStep) return null;

    return (
        <div className="flex flex-col gap-6 animate-fade-in">
            {/* Page Header integrated like QuickWizard style but page content */}
            <div className="bg-white dark:bg-slate-800 rounded-2xl p-6 border border-slate-100 dark:border-slate-700 shadow-sm flex items-center justify-between">
                <div className="flex items-center gap-4">
                    <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center text-primary">
                        <span className="material-symbols-outlined text-3xl">shopping_cart</span>
                    </div>
                    <div>
                        <h1 className="text-xl font-extrabold text-slate-800 dark:text-white tracking-tight">Akıllı Market Listesi</h1>
                        <p className="text-sm text-slate-500 font-medium">İhtiyacın olanları seç, listeni oluştur.</p>
                    </div>
                </div>
            </div>

            {/* MAIN WIZARD REPLICATING QUICKWIZARD EXACTLY */}
            <div className="bg-white dark:bg-slate-800 w-full h-[700px] rounded-3xl shadow-2xl flex flex-col md:flex-row overflow-hidden relative ring-1 ring-white/10 border border-slate-100 dark:border-slate-700">

                {/* LEFT RAIL: Navigation */}
                <aside className="md:w-[80px] md:border-r border-b md:border-b-0 border-slate-100 dark:border-slate-700 bg-white dark:bg-slate-800 flex flex-row md:flex-col items-center justify-between md:justify-start p-4 md:py-6 z-20 shrink-0">
                    <div className="hidden md:flex flex-col gap-4 w-full">
                        {suggestedSteps.map((step, idx) => {
                            const isActive = idx === currentStepIndex;
                            const isCompleted = idx < currentStepIndex;
                            return (
                                <button
                                    key={idx}
                                    onClick={() => setCurrentStepIndex(idx)}
                                    className={`group w-full flex flex-col items-center gap-1 relative ${isActive ? 'text-primary' : isCompleted ? 'text-green-500' : 'text-slate-400'}`}
                                >
                                    <div className={`p-2.5 rounded-xl transition-all duration-300 ${isActive ? 'bg-red-50 dark:bg-red-900/20 ring-1 ring-red-100 dark:ring-red-900/40 shadow-sm' : ''}`}>
                                        <span className="material-symbols-outlined text-[24px]">{isCompleted ? 'check_circle' : step.icon}</span>
                                    </div>
                                    <span className="text-[9px] font-bold tracking-wide text-center hidden md:block">{step.title.split(' ')[0]}</span>
                                    {isActive && <div className="hidden md:block absolute -right-[17px] top-1/2 -translate-y-1/2 w-1 h-8 bg-primary rounded-l-full"></div>}
                                </button>
                            );
                        })}
                    </div>
                    {/* Mobile Hero Progress */}
                    <div className="md:hidden flex items-center gap-2 w-full">
                        <span className="material-symbols-outlined text-primary text-2xl">{currentStep.icon}</span>
                        <div className="flex-1">
                            <h2 className="text-sm font-bold text-slate-800 dark:text-white">{currentStep.title}</h2>
                        </div>
                    </div>
                </aside>

                {/* MIDDLE: Main Content */}
                <main className="flex-1 flex flex-col h-full overflow-hidden bg-slate-50 dark:bg-[#0f172a] relative">
                    <header className="hidden md:flex bg-white dark:bg-slate-800 border-b border-slate-100 dark:border-slate-700 px-8 py-6 items-center justify-between shrink-0">
                        <div>
                            <h2 className="text-xl font-extrabold text-slate-800 dark:text-white tracking-tight">{currentStep.isFinal ? 'Listeyi Onayla' : currentStep.title}</h2>
                            <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">{currentStep.isFinal ? 'Her şey hazır mı şefim?' : 'İstediğin ürünlerin miktarını belirlemek için tıkla.'}</p>
                        </div>
                        <div className="flex items-center gap-2 bg-slate-100 dark:bg-slate-700/50 px-3 py-1.5 rounded-full">
                            <span className="w-2 h-2 rounded-full bg-primary animate-pulse"></span>
                            <span className="text-xs font-bold text-slate-600 dark:text-slate-300">{currentStepIndex + 1}/{suggestedSteps.length} Step</span>
                        </div>
                    </header>

                    <div className="flex-1 overflow-y-auto p-4 md:p-8 custom-scrollbar">
                        {!currentStep.isFinal ? (
                            <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3 md:gap-4">
                                {currentStep.items.map(item => {
                                    const isSelected = !!selections[item.ingredientId];
                                    const priceLabel = priceLabelsEnabled ? resolvePriceStatusLabel(item) : null;
                                    return (
                                        <button
                                            key={item.ingredientId}
                                            onClick={() => openItemModal(item)}
                                            className={`group relative flex flex-col items-center p-2 md:p-3 rounded-2xl border transition-all duration-200 h-[170px] md:h-36
                                                ${isSelected
                                                    ? 'bg-red-50 dark:bg-red-900/10 border-primary ring-1 ring-primary shadow-sm'
                                                    : 'bg-white dark:bg-slate-800 border-slate-100 dark:border-slate-700 hover:border-red-200 dark:hover:border-red-800 shadow-sm hover:shadow-md'
                                                }`}
                                        >
                                            {/* Selection Checkmark */}
                                            {isSelected && (
                                                <div className="absolute top-2 right-2 size-5 bg-primary text-white rounded-full flex items-center justify-center shadow-sm z-10 animate-scale-in">
                                                    <span className="material-symbols-outlined text-[14px]">check</span>
                                                </div>
                                            )}

                                            <div className={`w-full flex-1 flex items-center justify-center p-2 mb-2 bg-white dark:bg-slate-700/30 rounded-xl ${!isSelected && 'border border-dashed border-slate-200 dark:border-slate-600'}`}>
                                                <span className={`material-symbols-outlined text-[32px] transition-colors ${isSelected ? 'text-primary' : 'text-slate-300 dark:text-slate-500 group-hover:text-primary/70'}`}>
                                                    {item.isCritical ? 'warning' : getCategoryIcon(currentStep.title)}
                                                </span>
                                            </div>

                                            <span className={`text-xs font-bold transition-colors text-center truncate w-full ${isSelected ? 'text-primary' : 'text-slate-700 dark:text-slate-200'}`}>
                                                {item.name}
                                            </span>

                                            {priceLabel ? (
                                                <span className={`mt-1 text-[9px] font-bold px-2 py-0.5 rounded-full ${getPriceStatusBadgeClass(priceLabel)}`}>
                                                    {priceLabel}
                                                </span>
                                            ) : null}

                                            {item.rounding?.applied && getSuggestedPurchaseLabel(item) ? (
                                                <span className="mt-1 text-[9px] font-bold text-emerald-700 bg-emerald-50 px-2 py-0.5 rounded-full shadow-sm">
                                                    Al: {getSuggestedPurchaseLabel(item)}
                                                </span>
                                            ) : null}

                                            {isSelected && (
                                                <span className="text-[9px] font-bold text-white bg-primary px-2 py-0.5 rounded-full mt-1 shadow-sm">
                                                    {selections[item.ingredientId].amount} {selections[item.ingredientId].unit.toUpperCase()}
                                                </span>
                                            )}
                                        </button>
                                    );
                                })}
                            </div>
                        ) : (
                            <div className="flex flex-col items-center justify-center h-full text-center p-8">
                                <div className="bg-red-50 dark:bg-red-900/10 p-6 rounded-full mb-6">
                                    <span className="material-symbols-outlined text-5xl text-primary">fact_check</span>
                                </div>
                                <h3 className="text-2xl font-black text-slate-800 dark:text-white mb-4">LİSTENİZ HAZIR!</h3>
                                <div className="w-full max-w-sm space-y-4">
                                    <div className="text-left">
                                        <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-2">LİSTE ADI</label>
                                        <input
                                            type="text"
                                            value={listName}
                                            onChange={(e) => setListName(e.target.value)}
                                            className="w-full bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-2xl p-4 font-bold text-center outline-none focus:ring-2 ring-primary/20 transition-all"
                                        />
                                    </div>
                                    <div className="grid grid-cols-2 gap-3">
                                        <div className="bg-white dark:bg-slate-800 p-4 rounded-2xl border border-slate-100 dark:border-slate-700">
                                            <p className="text-[10px] font-bold text-slate-400 uppercase">Ürün Sayısı</p>
                                            <p className="text-xl font-black text-slate-800 dark:text-white">{Object.keys(selections).length}</p>
                                        </div>
                                        <div className="bg-white dark:bg-slate-800 p-4 rounded-2xl border border-slate-100 dark:border-slate-700">
                                            <p className="text-[10px] font-bold text-slate-400 uppercase">Kritik Eksik</p>
                                            <p className="text-xl font-black text-red-500">{Object.values(selections).filter(i => i.isCritical).length}</p>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                </main>

                {/* RIGHT: Summary Area */}
                <aside className="absolute bottom-0 left-0 right-0 w-full md:static md:w-[320px] bg-white dark:bg-slate-800 md:border-l border-t md:border-t-0 border-slate-100 dark:border-slate-700 z-30 shrink-0 shadow-[0_-4px_20px_rgba(0,0,0,0.05)] md:shadow-none flex flex-col">
                    <div className="hidden md:flex p-5 border-b border-slate-100 dark:border-slate-700 items-center justify-between shrink-0">
                        <h3 className="font-bold text-slate-800 dark:text-white uppercase tracking-tighter text-sm">SEÇİLENLER ({Object.keys(selections).length})</h3>
                        {Object.keys(selections).length > 0 && <button onClick={() => setSelections({})} className="text-[10px] text-primary font-bold hover:underline">TEMİZLE</button>}
                    </div>

                    <div className="hidden md:block flex-1 overflow-y-auto p-4 space-y-2 custom-scrollbar">
                        {Object.keys(selections).length === 0 ? (
                            <div className="h-full flex flex-col items-center justify-center text-slate-300 dark:text-slate-600">
                                <span className="material-symbols-outlined text-[48px] mb-2 opacity-50">shopping_basket</span>
                                <p className="text-xs font-bold text-center px-6">Listeniz henüz boş. Ürün eklemek için kutulara tıklayın.</p>
                            </div>
                        ) : (
                            Object.values(selections).map((sel, idx) => (
                                <div key={sel.ingredientId} className="flex items-center justify-between p-3 bg-slate-50 dark:bg-slate-700/30 rounded-xl border border-slate-100 dark:border-slate-700 animate-in fade-in slide-in-from-right-4">
                                    <div className="flex items-center gap-3">
                                        <div className="size-8 rounded-lg bg-white dark:bg-slate-600 flex items-center justify-center text-primary shadow-sm font-bold text-xs ring-1 ring-black/5">
                                            {sel.name.charAt(0)}
                                        </div>
                                        <div className="flex flex-col">
                                            <span className="text-xs font-bold text-slate-800 dark:text-slate-200 truncate max-w-[120px]">{sel.name}</span>
                                            <span className="text-[10px] text-primary font-bold">{sel.amount} {sel.unit.toUpperCase()}</span>
                                            {sel.rounding?.applied && getSuggestedPurchaseLabel(sel) ? (
                                                <span className="text-[10px] text-emerald-600 font-bold">Önerilen alım: {getSuggestedPurchaseLabel(sel)}</span>
                                            ) : null}
                                        </div>
                                    </div>
                                    <button onClick={() => handleRemove(sel.ingredientId)} className="text-slate-400 hover:text-red-500 p-1 rounded-lg transition-colors">
                                        <span className="material-symbols-outlined text-[18px]">close</span>
                                    </button>
                                </div>
                            ))
                        )}
                    </div>

                    <div className="p-4 md:p-6 bg-slate-50 dark:bg-slate-800 md:dark:bg-slate-900/30 border-t border-slate-100 dark:border-slate-700">
                        {currentStepIndex < suggestedSteps.length - 1 ? (
                            <button
                                onClick={() => setCurrentStepIndex(currentStepIndex + 1)}
                                className="w-full bg-primary hover:bg-red-600 text-white py-4 rounded-xl font-bold text-sm shadow-lg shadow-primary/20 transition-all flex items-center justify-center gap-2 active:scale-95"
                            >
                                <span>SONRAKİ GRUP</span>
                                <span className="material-symbols-outlined text-[20px]">arrow_forward</span>
                            </button>
                        ) : (
                            <button
                                onClick={handleSave}
                                disabled={isSubmitting || Object.keys(selections).length === 0}
                                className="w-full bg-primary hover:bg-red-600 text-white py-4 rounded-xl font-bold text-sm shadow-lg shadow-primary/20 transition-all flex items-center justify-center gap-2 active:scale-95 disabled:opacity-50"
                            >
                                <span>{isSubmitting ? 'KAYDEDİLİYOR...' : 'LİSTEYİ TAMAMLA'}</span>
                                {!isSubmitting && <span className="material-symbols-outlined text-[20px]">check</span>}
                            </button>
                        )}
                        <button onClick={() => navigate('/dashboard')} className="w-full text-center text-[10px] font-bold text-slate-400 uppercase tracking-widest mt-4 hover:text-red-500 transition-colors">Vazgeç</button>
                    </div>
                </aside>

                {/* QUANTITY MODAL REPLICATING QUICKWIZARD MODAL STYLE */}
                <AnimatePresence>
                    {modalItem && (
                        <div
                            className="absolute inset-0 z-[100] bg-slate-900/40 backdrop-blur-[2px] flex items-center justify-center p-4"
                            onClick={closeItemModal}
                        >
                            <motion.div
                                initial={{ scale: 0.9, opacity: 0 }}
                                animate={{ scale: 1, opacity: 1 }}
                                onClick={(e) => e.stopPropagation()}
                                className="bg-white dark:bg-slate-800 w-full max-w-[340px] rounded-2xl shadow-2xl border border-slate-100 dark:border-slate-600 p-6 flex flex-col items-center gap-5 relative"
                            >
                                <button onClick={closeItemModal} className="absolute top-4 right-4 text-slate-400 hover:text-slate-600 dark:hover:text-white">
                                    <span className="material-symbols-outlined">close</span>
                                </button>

                                <div className="size-16 bg-red-50 dark:bg-red-900/20 rounded-full flex items-center justify-center text-primary mb-1 ring-4 ring-red-50 dark:ring-red-900/10">
                                    <span className="material-symbols-outlined text-[32px]">{getCategoryIcon(modalItem.category || '')}</span>
                                </div>

                                <div className="text-center">
                                    <h3 className="font-extrabold text-slate-800 dark:text-white text-xl uppercase tracking-tighter">{modalItem.name}</h3>
                                    <p className="text-xs text-slate-500 font-medium mt-1">İhtiyacın olan miktarı belirle.</p>
                                    {modalPriceLabel ? (
                                        <>
                                            <span className={`inline-flex mt-2 text-[10px] font-bold px-2 py-0.5 rounded-full ${getPriceStatusBadgeClass(modalPriceLabel)}`}>
                                                {modalPriceLabel}
                                            </span>
                                            <p className="text-[11px] text-slate-500 dark:text-slate-300 mt-2">
                                                {getPriceStatusMessage(modalPriceLabel)}
                                            </p>
                                        </>
                                    ) : null}
                                </div>

                                <div className="w-full space-y-4">
                                    <div className="flex gap-2">
                                        <input
                                            type="number"
                                            autoFocus
                                            defaultValue={selections[modalItem.ingredientId]?.amount || modalItem.suggestedAmount || 1}
                                            onKeyDown={(e) => e.key === 'Enter' && handleQuantityConfirm(e.target.value, document.getElementById('q-unit').value)}
                                            className="flex-1 bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl p-3 text-2xl font-black text-center outline-none focus:ring-2 ring-primary/20"
                                            id="q-amount"
                                        />
                                        <select
                                            id="q-unit"
                                            defaultValue={selections[modalItem.ingredientId]?.unit || modalItem.unit || 'adet'}
                                            className="w-24 bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl p-3 font-bold outline-none"
                                        >
                                            {UNIDADES.map(u => <option key={u} value={u}>{u}</option>)}
                                        </select>
                                    </div>
                                    <div className="bg-blue-50 dark:bg-blue-900/10 p-4 rounded-xl text-[10px] font-bold text-blue-600 flex gap-2">
                                        <span className="material-symbols-outlined text-[16px]">info</span>
                                        <div className="space-y-1">
                                            <p>Gerçek ihtiyaç: {getNeededLabel(modalItem)}</p>
                                            {modalItem.rounding?.applied && getSuggestedPurchaseLabel(modalItem) ? (
                                                <p>Markette alınacak form: {getSuggestedPurchaseLabel(modalItem)}</p>
                                            ) : (
                                                <p>Zeka önerisi: {formatShoppingDisplayAmount(modalItem.suggestedAmount, modalItem.unit)}</p>
                                            )}
                                        </div>
                                    </div>
                                </div>

                                <button
                                    onClick={() => handleQuantityConfirm(document.getElementById('q-amount').value, document.getElementById('q-unit').value)}
                                    className="w-full bg-primary text-white py-4 rounded-xl font-bold text-sm shadow-lg shadow-primary/20 hover:scale-[1.02] active:scale-[0.98] transition-all"
                                >
                                    FİLTREYE EKLE
                                </button>
                            </motion.div>
                        </div>
                    )}
                </AnimatePresence>

            </div>

            {/* Bottom Insight Section */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="col-span-1 md:col-span-2 bg-slate-900 p-6 rounded-2xl text-white flex items-center gap-4 shadow-xl border border-white/5">
                    <div className="w-12 h-12 bg-white/10 rounded-xl flex items-center justify-center shrink-0">
                        <span className="material-symbols-outlined text-2xl text-primary font-black italic">lightbulb</span>
                    </div>
                    <div>
                        <h4 className="font-bold text-sm uppercase italic">Akıllı Mutfak Zekası</h4>
                        <p className="text-slate-400 text-[11px] font-medium leading-relaxed italic">Bu liste sadece eksiği değil; evdeki kişi sayısı, bebek hassasiyeti ve haftalık tüketim hızını analiz ederek sana en doğru miktarı önerir.</p>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ShoppingList;
